import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ActiveFilters } from "@/components/shared/FilterBar";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowRight,
  Boxes,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Clock,
  Eye,
  Flame,
  Image as ImageIcon,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Repeat,
  Sparkles,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumericInput } from "@/components/ui/numeric-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { DatePeriodFilter, useDatePeriodFilter } from "@/components/shared/DatePeriodFilter";
import { EntityLink } from "@/components/shared/EntityLink";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import {
  CHECKBOX_COL_NULL,
  InlineFilterRow,
  type FilterField,
} from "@/components/shared/InlineFilterRow";
import { ListPageShell } from "@/components/shared/ListPageShell";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useRowSelection } from "@/hooks/useRowSelection";
import { exportToExcel } from "@/lib/excel.utils";
import { trpcClient } from "@/lib/trpc";
import { getFullUrl } from "@/lib/uploadFile";
import { cn, formatDate, formatNumber } from "@/lib/utils";
import { PrintLayoutModalContainer } from "./PrintLayoutModalContainer";
import {
  computeDetailUrgency,
  extractMaterialSignature,
  extractRoutingSignature,
  resolveOrderCode,
  resolveRemainingPrepressQty,
  validateBulkImpositionSelection,
} from "./prepress-imposition.helper";
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

export type PrepressHubTab =
  | "cho_binh"
  | "cho_tao_lenh"
  | "da_tao_lenh"
  | "yeu_cau_binh_lai"
  | "tat_ca_bai_binh";

function normalizeHubTab(raw?: string | null): PrepressHubTab {
  const v = String(raw || "").trim().toLowerCase();
  if (v === "cho_tao_lenh" || v === "planned" || v === "waiting_po") return "cho_tao_lenh";
  if (v === "da_tao_lenh" || v === "po_created") return "da_tao_lenh";
  if (v === "yeu_cau_binh_lai" || v === "re_prepress") return "yeu_cau_binh_lai";
  if (v === "tat_ca_bai_binh" || v === "library" || v === "all") return "tat_ca_bai_binh";
  return "cho_binh";
}

const ORDER_DETAIL_FILTER_COLUMNS: (FilterField | typeof CHECKBOX_COL_NULL | null)[] = [
  CHECKBOX_COL_NULL,
  { key: "orderCode", label: "Lọc mã ĐH...", type: "text" },
  { key: "customer", label: "Lọc khách hàng...", type: "text" },
  { key: "product", label: "Lọc sản phẩm...", type: "text" },
  null,
  { key: "prepressCode", label: "Lọc mã bài...", type: "text" },
  null,
  null,
];

const ORDER_DETAIL_ACTIVE_FILTERS: FilterField[] = ORDER_DETAIL_FILTER_COLUMNS.filter(
  (f): f is FilterField => Boolean(f && "key" in f && (f as FilterField).key),
);

const PRINT_LAYOUT_FILTER_COLUMNS: (FilterField | typeof CHECKBOX_COL_NULL | null)[] = [
  CHECKBOX_COL_NULL,
  { key: "layoutCode", label: "Lọc mã bài...", type: "text" },
  { key: "orderCode", label: "Lọc ĐH / KH...", type: "text" },
  { key: "product", label: "Lọc sản phẩm...", type: "text" },
  { key: "sheetSize", label: "Lọc khổ giấy...", type: "text" },
  null,
  null,
  null,
  null,
  null,
];

const PRINT_LAYOUT_ACTIVE_FILTERS: FilterField[] = PRINT_LAYOUT_FILTER_COLUMNS.filter(
  (f): f is FilterField => Boolean(f && "key" in f && (f as FilterField).key),
);

const URGENCY_OPTIONS = [
  { key: "ALL", label: "Tất cả hạn" },
  { key: "EXPRESS", label: "Hỏa tốc" },
  { key: "URGENT", label: "Đơn gấp" },
  { key: "DUE_SOON", label: "Cận hạn" },
  { key: "OVERDUE", label: "Quá hạn" },
] as const;

const BATCH_TYPE_OPTIONS = [
  { key: "ALL", label: "Tất cả loại bài" },
  { key: "SINGLE", label: "Bài đơn" },
  { key: "GANG_RUN", label: "Bài ghép" },
  { key: "SPLIT_RUN", label: "Tách bài" },
] as const;

function renderUrgencyBadge(detail: any) {
  const urgency = computeDetailUrgency(detail);
  if (urgency === "OVERDUE") {
    return (
      <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px] font-bold px-1.5 py-0">
        Quá hạn
      </Badge>
    );
  }
  if (urgency === "DUE_SOON") {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold px-1.5 py-0">
        Cận hạn
      </Badge>
    );
  }
  if (urgency === "EXPRESS") {
    return (
      <Badge className="bg-rose-600 text-white text-[10px] font-bold px-1.5 py-0">
        <Flame className="h-2.5 w-2.5 mr-0.5 inline" /> Hỏa tốc
      </Badge>
    );
  }
  if (urgency === "URGENT") {
    return (
      <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-[10px] font-bold px-1.5 py-0">
        Đơn gấp
      </Badge>
    );
  }
  return null;
}

/** Trạng thái lọc theo cột (ẩn / hiện hàng lọc, giá trị lọc) cho bảng tự tải dữ liệu */
function useInlineFilterState() {
  const [filterValues, setFilterValues] = useState<ActiveFilters>({});
  const [showFilter, setShowFilter] = useState(false);
  const onFilterChange = useCallback((next: ActiveFilters) => setFilterValues(next || {}), []);
  const onToggleFilter = useCallback(() => setShowFilter((v) => !v), []);
  const onClearAll = useCallback(() => setFilterValues({}), []);
  return { filterValues, showFilter, onFilterChange, onToggleFilter, onClearAll };
}

export default function LayoutList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = useSearch({ strict: false }) as { tab?: string };
  const { headerSearch: searchQuery } = useHeaderSearch();
  const { hasPermission } = useRoleAccess();
  const canEdit =
    hasPermission("prepress", "update") ||
    hasPermission("orders", "update") ||
    hasPermission("production_orders", "update");
  const canDelete =
    hasPermission("prepress", "delete") ||
    hasPermission("production_orders", "delete");

  // Active tab synced with URL ?tab=... (Rule 3.3 Anti-Tab Trap: replace: true)
  const [activeTab, setActiveTabState] = useState<PrepressHubTab>(() =>
    normalizeHubTab(searchParams?.tab),
  );

  useEffect(() => {
    if (searchParams?.tab) {
      const next = normalizeHubTab(searchParams.tab);
      setActiveTabState((prev) => (prev === next ? prev : next));
    }
  }, [searchParams?.tab]);

  // Date filter at Outermost Left Position 0 (Rule 12)
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter("all");

  // Secondary filter pills & pagination
  const [urgencyFilter, setUrgencyFilter] = useState<string>("ALL");
  const [batchTypeFilter, setBatchTypeFilter] = useState<"ALL" | "SINGLE" | "GANG_RUN" | "SPLIT_RUN">("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);
  const [expandedLayoutIds, setExpandedLayoutIds] = useState<Set<string>>(new Set());

  // Inline column filter state
  const isLayoutTableTab = activeTab === "cho_tao_lenh" || activeTab === "tat_ca_bai_binh";
  // Bộ lọc cột của 2 bảng chỉ là trạng thái tại chỗ (dữ liệu tải ở query riêng bên dưới);
  // trước đây gọi useServerListFilter với danh sách cột nên mỗi lần mở trang chạy 1 query hỏng
  const detailFilterCtrl = useInlineFilterState();
  const layoutFilterCtrl = useInlineFilterState();

  // Modal & Dialog state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [modalLayoutId, setModalLayoutId] = useState<string | null>(null);
  const [modalDetails, setModalDetails] = useState<any[]>([]);
  const [modalOrderCode, setModalOrderCode] = useState<string | null>(null);
  const [modalFocusField, setModalFocusField] = useState<
    "code" | "sheetSize" | "ups" | "sheets" | null
  >(null);

  const [zoomProofUrl, setZoomProofUrl] = useState<string | null>(null);
  const [layoutToDelete, setLayoutToDelete] = useState<{ id: string; code: string } | null>(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);

  // Quick Resolve Re-Prepress Dialog state (Tab 4)
  const [rePrepressTarget, setRePrepressTarget] = useState<any | null>(null);
  const [reForm, setReForm] = useState({
    prepressCode: "",
    sheetSize: "",
    ups: 1,
    printSheets: 1,
    notes: "",
  });

  // 1. OrderDetail Pool Query (for Tabs: Chờ bình, Đã tạo lệnh, Yêu cầu bình lại)
  const apiOrderDetailTab = useMemo(() => {
    if (activeTab === "da_tao_lenh") return "DA_TAO_LENH" as const;
    if (activeTab === "yeu_cau_binh_lai") return "YEU_CAU_BINH_LAI" as const;
    return "CHO_BINH" as const;
  }, [activeTab]);

  const { data: detailsData, isLoading: isDetailsLoading } = useQuery({
    queryKey: [
      "prepress-hub-list",
      apiOrderDetailTab,
      currentPage,
      pageSize,
      searchQuery,
      dateParams.from || "",
      dateParams.to || "",
      urgencyFilter,
      detailFilterCtrl.filterValues,
    ],
    queryFn: () =>
      trpcClient.prepress.listPendingOrderDetails.query({
        tab: apiOrderDetailTab,
        page: currentPage,
        limit: pageSize,
        search: searchQuery.trim() || undefined,
        fromDate: dateParams.from,
        toDate: dateParams.to,
        urgency: urgencyFilter !== "ALL" ? urgencyFilter : undefined,
        filters: detailFilterCtrl.filterValues as Record<string, string>,
      }),
    enabled: !isLayoutTableTab,
  });

  // 2. PrintLayout Workbench Query (for Tabs: Chờ tạo lệnh, Tất cả bài bình)
  const layoutStatusParam = activeTab === "cho_tao_lenh" ? "WAITING_PO" : "ALL";
  const { data: layoutsData, isLoading: isLayoutsLoading } = useQuery({
    queryKey: [
      "print-layouts-workbench",
      layoutStatusParam,
      currentPage,
      pageSize,
      searchQuery,
      dateParams.from || "",
      dateParams.to || "",
      batchTypeFilter,
      layoutFilterCtrl.filterValues,
    ],
    queryFn: () =>
      trpcClient.prepress.listPrintLayouts.query({
        status: layoutStatusParam,
        page: currentPage,
        limit: pageSize,
        search: searchQuery.trim() || undefined,
        fromDate: dateParams.from,
        toDate: dateParams.to,
        batchType: batchTypeFilter,
        filters: layoutFilterCtrl.filterValues as Record<string, string>,
      }),
    enabled: isLayoutTableTab,
  });

  // Preserve latest 5-tab badge counts from active workbench query (zero duplicate DB queries)
  const [cachedCounts, setCachedCounts] = useState<any>({});
  useEffect(() => {
    const incoming = (isLayoutTableTab ? (layoutsData as any)?.counts : (detailsData as any)?.counts) || null;
    if (incoming) {
      setCachedCounts(incoming);
    }
  }, [isLayoutTableTab, layoutsData, detailsData]);

  const tabCounts = useMemo(() => {
    const c =
      (isLayoutTableTab ? (layoutsData as any)?.counts : (detailsData as any)?.counts) ||
      cachedCounts ||
      {};
    return {
      choBinh: Number(c.choBinh ?? c.ready ?? 0),
      choTaoLenh: Number(c.choTaoLenh ?? c.planned ?? 0),
      daTaoLenh: Number(c.daTaoLenh ?? c.poCreated ?? 0),
      yeuCauBinhLai: Number(c.yeuCauBinhLai ?? c.rePrepress ?? 0),
      tatCaBaiBinh: Number(c.tatCaBaiBinh ?? 0),
    };
  }, [isLayoutTableTab, layoutsData, detailsData, cachedCounts]);

  const detailItems: any[] = useMemo(() => (detailsData as any)?.items || [], [detailsData]);
  const layoutItems: any[] = useMemo(() => (layoutsData as any)?.items || [], [layoutsData]);

  const totalItems = isLayoutTableTab
    ? Number((layoutsData as any)?.total || 0)
    : Number((detailsData as any)?.total || 0);

  // Row selection (Rule 9: auto-reset when tab/filter/search changes)
  const activeRowIds = useMemo(
    () =>
      isLayoutTableTab
        ? layoutItems.map((l) => String(l.id))
        : detailItems.map((d) => String(d.id)),
    [isLayoutTableTab, layoutItems, detailItems],
  );

  const rowSelection = useRowSelection(activeRowIds);
  const { selectedIds, selectedCount, clearSelection } = rowSelection;

  useEffect(() => {
    clearSelection();
  }, [
    activeTab,
    searchQuery,
    period,
    customFrom,
    customTo,
    urgencyFilter,
    batchTypeFilter,
    clearSelection,
  ]);

  const handleSwitchTab = useCallback(
    (nextTab: PrepressHubTab) => {
      setActiveTabState(nextTab);
      setCurrentPage(1);
      clearSelection();
      navigate({
        to: "/layouts",
        search: ((prev: any) => ({ ...prev, tab: nextTab })) as any,
        replace: true,
      });
    },
    [clearSelection, navigate],
  );

  const refreshAllPrepressQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["prepress-hub-list"] }),
      queryClient.invalidateQueries({ queryKey: ["print-layouts-workbench"] }),
      queryClient.invalidateQueries({ queryKey: ["print-layout-detail"] }),
    ]);
  }, [queryClient]);

  // Mutations
  const deleteLayoutMutation = useMutation({
    mutationFn: (id: string) => trpcClient.prepress.deletePrintLayout.mutate({ id }),
    onSuccess: async () => {
      toast.success("Đã xóa bài bình");
      setLayoutToDelete(null);
      await refreshAllPrepressQueries();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Không thể xóa bài bình");
    },
  });

  const bulkDeleteLayoutsMutation = useMutation({
    mutationFn: (ids: string[]) => trpcClient.prepress.bulkDeletePrintLayouts.mutate({ ids }),
    onSuccess: async (report: any) => {
      const okCount = Number(report?.successCount || 0);
      const skipCount = Number(report?.skippedCount || 0);
      if (okCount > 0 && skipCount === 0) {
        toast.success(`Đã xóa ${okCount} bài bình`);
      } else if (okCount > 0 && skipCount > 0) {
        toast.warning(
          `Đã xóa ${okCount} bài bình, bỏ qua ${skipCount} bài đã tạo Lệnh SX.`,
        );
      } else {
        toast.warning("Không thể xóa các bài bình đã tạo Lệnh SX.");
      }
      setBulkDeleteConfirmOpen(false);
      clearSelection();
      await refreshAllPrepressQueries();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Lỗi khi xóa hàng loạt bài bình");
    },
  });

  const markCompleteMutation = useMutation({
    mutationFn: ({ ids, completed }: { ids: string[]; completed: boolean }) =>
      trpcClient.prepress.markOrderDetailPrepressComplete.mutate({
        orderDetailIds: ids,
        completed,
      }),
    onSuccess: async (_res, vars) => {
      toast.success(
        vars.completed
          ? "Đã đánh dấu hoàn tất bình file"
          : "Đã mở lại trạng thái chờ bình file",
      );
      clearSelection();
      await refreshAllPrepressQueries();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Không thể cập nhật trạng thái bình file");
    },
  });


  const resolveRePrepressMutation = useMutation({
    mutationFn: (payload: {
      detailIds: string[];
      prepressCode?: string;
      sheetSize?: string;
      ups?: number;
      printSheets?: number;
      notes?: string;
    }) => trpcClient.prepress.resolveRePrepress.mutate(payload),
    onSuccess: async () => {
      toast.success("Đã xác nhận hoàn tất xử lý bình lại");
      setRePrepressTarget(null);
      await refreshAllPrepressQueries();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Không thể xác nhận hoàn tất bình lại");
    },
  });

  // Open Canonical PrintLayoutModalContainer helpers
  const openImpositionForSingleDetail = useCallback(
    (
      detail: any,
      focus: "code" | "sheetSize" | "ups" | "sheets" | null = null,
    ) => {
      setModalMode("create");
      setModalLayoutId(null);
      setModalDetails([detail]);
      setModalOrderCode(resolveOrderCode(detail) || null);
      setModalFocusField(focus);
      setModalOpen(true);
    },
    [],
  );

  const openEditLayoutModal = useCallback((layout: any) => {
    setModalMode("edit");
    setModalLayoutId(String(layout.id));
    setModalDetails([]);
    setModalOrderCode(layout.orderCode || null);
    setModalFocusField(null);
    setModalOpen(true);
  }, []);

  const handleBulkImposeSelected = useCallback(() => {
    const chosen = detailItems.filter((d) => selectedIds.has(String(d.id)));
    if (chosen.length === 0) return;

    const check = validateBulkImpositionSelection(chosen);
    if (!check.ok) {
      toast.warning(check.reason || "Các sản phẩm đã chọn không đủ điều kiện ghép chung 1 bài in!");
      return;
    }

    setModalMode("create");
    setModalLayoutId(null);
    setModalDetails(chosen);
    setModalOrderCode(resolveOrderCode(chosen[0]) || null);
    setModalFocusField(null);
    setModalOpen(true);
  }, [detailItems, selectedIds]);

  // Expand / Collapse child items on PrintLayout tabs
  const toggleExpandLayout = useCallback((id: string) => {
    setExpandedLayoutIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allLayoutsExpanded =
    layoutItems.length > 0 && layoutItems.every((l) => expandedLayoutIds.has(String(l.id)));

  const handleToggleExpandAll = useCallback(() => {
    if (allLayoutsExpanded) {
      setExpandedLayoutIds(new Set());
    } else {
      setExpandedLayoutIds(new Set(layoutItems.map((l) => String(l.id))));
    }
  }, [allLayoutsExpanded, layoutItems]);

  // Bulk Action Bar items per tab (Rule 9)
  const bulkActions = useMemo<BulkAction[]>(() => {
    if (selectedCount === 0) return [];
    const selectedIdList = Array.from(selectedIds);

    if (activeTab === "cho_binh") {
      const actions: BulkAction[] = [
        {
          label:
            selectedCount > 1
              ? `Bình File / Ghép Bài (${selectedCount})`
              : "Bình File (1)",
          icon: <Zap className="h-3.5 w-3.5" />,
          onClick: handleBulkImposeSelected,
          variant: "default",
        },
        {
          label: "Đánh dấu đã bình đủ",
          icon: <CheckCircle2 className="h-3.5 w-3.5" />,
          onClick: () =>
            markCompleteMutation.mutate({ ids: selectedIdList, completed: true }),
          variant: "outline",
        },
      ];
      return actions;
    }

    if (activeTab === "cho_tao_lenh" || activeTab === "tat_ca_bai_binh") {
      const chosenLayouts = layoutItems.filter((l) => selectedIds.has(String(l.id)));
      const waitingLayouts = chosenLayouts.filter((l) => !l.productionOrder);
      const actions: BulkAction[] = [];

      if (waitingLayouts.length === 1 && chosenLayouts.length === 1) {
        actions.push({
          label: "Tạo Lệnh SX",
          icon: <Boxes className="h-3.5 w-3.5" />,
          onClick: () =>
            navigate({
              to: "/production-orders/create",
              search: { layoutId: waitingLayouts[0].id } as any,
            }),
          variant: "default",
        });
      }
      // Nhiều bài bình: tạo từng lệnh qua form (máy chủ đã ngừng tạo lệnh tự động hàng loạt vì bỏ qua công đoạn sản phẩm)

      if (canDelete) {
        actions.push({
          label: `Xóa bài bình (${selectedCount})`,
          icon: <Trash2 className="h-3.5 w-3.5" />,
          onClick: () => setBulkDeleteConfirmOpen(true),
          variant: "destructive",
        });
      }
      return actions;
    }

    return [];
  }, [
    activeTab,
    canDelete,
    handleBulkImposeSelected,
    layoutItems,
    markCompleteMutation,
    navigate,
    selectedCount,
    selectedIds,
  ]);

  // Excel Export handler
  const handleExportExcel = useCallback(async () => {
    if (isLayoutTableTab) {
      const rows = layoutItems.map((l) => ({
        layoutCode: l.layoutCode || "",
        orderCode: l.orderCode || "",
        customerName: l.customerName || "",
        batchType:
          l.batchType === "GANG_RUN"
            ? `Bài ghép (${l.itemCount} SP)`
            : l.batchType === "SPLIT_RUN"
              ? "Tách bài"
              : "Bài đơn",
        sheetSize: l.sheetSize || "",
        totalUps: l.totalUps || l.ups || 1,
        sheetsPerUp: l.sheetsPerUp || l.totalPrintSheets || 0,
        totalPlannedQty: l.totalPlannedQty || 0,
        creatorName: l.creatorName || "",
        poCode: l.productionOrder?.orderId || "Chờ tạo lệnh",
      }));
      await exportToExcel(
        rows,
        [
          { key: "layoutCode", label: "Mã Bài Bình", width: 18 },
          { key: "orderCode", label: "Mã Đơn Hàng", width: 16 },
          { key: "customerName", label: "Khách Hàng", width: 28 },
          { key: "batchType", label: "Phân Loại", width: 16 },
          { key: "sheetSize", label: "Khổ Giấy In", width: 18 },
          { key: "totalUps", label: "Tổng Số Ups", width: 14 },
          { key: "sheetsPerUp", label: "SL Tờ In / Ups", width: 16 },
          { key: "totalPlannedQty", label: "Tổng SL SX", width: 16 },
          { key: "creatorName", label: "Người Bình File", width: 20 },
          { key: "poCode", label: "Lệnh Sản Xuất", width: 18 },
        ],
        `Danh_sach_bai_binh_${activeTab}`,
      );
      return;
    }

    const rows = detailItems.map((d) => ({
      orderCode: resolveOrderCode(d),
      customerName: d.order?.customer?.name || "",
      productName: d.productName || d.product?.productName || "",
      material: extractMaterialSignature(d).label,
      quantity: Number(d.quantity || 0),
      remainingPrepressQty: resolveRemainingPrepressQty(d),
      prepressCode: d.prepressCode || "",
      paperSize: d.prepressPaperSize || "",
      ups: d.prepressUps || "",
      sheets: d.prepressPrintSheets || "",
    }));
    await exportToExcel(
      rows,
      [
        { key: "orderCode", label: "Mã Đơn Hàng", width: 16 },
        { key: "customerName", label: "Khách Hàng", width: 28 },
        { key: "productName", label: "Sản Phẩm", width: 28 },
        { key: "material", label: "Chất Liệu", width: 22 },
        { key: "quantity", label: "SL Đặt", width: 14 },
        { key: "remainingPrepressQty", label: "SL Còn Chờ Bình", width: 16 },
        { key: "prepressCode", label: "Mã Bài Bình", width: 18 },
        { key: "paperSize", label: "Khổ In", width: 16 },
        { key: "ups", label: "Số Ups", width: 10 },
        { key: "sheets", label: "SL Tờ In", width: 14 },
      ],
      `Binh_file_${activeTab}`,
    );
  }, [activeTab, detailItems, isLayoutTableTab, layoutItems]);

  const activeFilterCtrl = isLayoutTableTab ? layoutFilterCtrl : detailFilterCtrl;

  // Render 5 Header Tabs
  const tabsBar = (
    <div
      className="flex items-center gap-1 overflow-x-auto no-scrollbar"
      role="tablist"
      aria-label="Phân loại trạng thái bình file"
    >
      {[
        {
          id: "cho_binh" as const,
          label: "Chờ bình",
          count: tabCounts.choBinh,
          badgeCls: "bg-amber-100 text-amber-800",
        },
        {
          id: "cho_tao_lenh" as const,
          label: "Chờ tạo lệnh",
          count: tabCounts.choTaoLenh,
          badgeCls: "bg-indigo-100 text-indigo-800",
        },
        {
          id: "da_tao_lenh" as const,
          label: "Đã tạo lệnh",
          count: tabCounts.daTaoLenh,
          badgeCls: "bg-emerald-100 text-emerald-800",
        },
        {
          id: "yeu_cau_binh_lai" as const,
          label: "Yêu cầu bình lại",
          count: tabCounts.yeuCauBinhLai,
          badgeCls: "bg-rose-100 text-rose-800",
        },
        {
          id: "tat_ca_bai_binh" as const,
          label: "Tất cả bài bình",
          count: tabCounts.tatCaBaiBinh,
          badgeCls: "bg-slate-200 text-slate-700",
        },
      ].map((t) => {
        const isActive = activeTab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={`prepress-tab-${t.id}`}
            onClick={() => handleSwitchTab(t.id)}
            className={emsTabClass(isActive, { size: "sm" })}
          >
            <span>{t.label}</span>
            <span
              className={cn(
                "px-1.5 py-0.2 rounded-full text-[10px] font-bold font-mono",
                isActive ? "bg-primary/10 text-primary" : t.badgeCls,
              )}
            >
              {formatNumber(t.count)}
            </span>
          </button>
        );
      })}
    </div>
  );

  // Render Row 2 Secondary Filter Pills
  const secondaryFilters = isLayoutTableTab ? (
    <div className="flex items-center gap-1.5 flex-wrap">
      {BATCH_TYPE_OPTIONS.map((opt) => {
        const active = batchTypeFilter === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => {
              setBatchTypeFilter(opt.key);
              setCurrentPage(1);
            }}
            className={cn(
              "h-7 px-2.5 rounded-md text-[11px] font-medium border transition-colors cursor-pointer",
              active
                ? "bg-indigo-50 text-indigo-700 border-indigo-300 font-semibold"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
            )}
          >
            {opt.label}
          </button>
        );
      })}
      {layoutItems.length > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleToggleExpandAll}
          className="h-7 px-2.5 text-[11px] gap-1 text-slate-700"
          data-testid="toggle-expand-all-layouts-btn"
        >
          <ChevronsUpDown className="h-3.5 w-3.5 text-indigo-600" />
          <span>{allLayoutsExpanded ? "Thu gọn chi tiết" : "Mở rộng tất cả"}</span>
        </Button>
      )}
    </div>
  ) : (
    <div className="flex items-center gap-1 flex-wrap">
      {URGENCY_OPTIONS.map((opt) => {
        const active = urgencyFilter === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => {
              setUrgencyFilter(opt.key);
              setCurrentPage(1);
            }}
            className={cn(
              "h-7 px-2.5 rounded-md text-[11px] font-medium border transition-colors cursor-pointer",
              active
                ? "bg-indigo-50 text-indigo-700 border-indigo-300 font-semibold"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <ListPageShell
        unitLabel={isLayoutTableTab ? "bài bình" : "chi tiết đơn"}
        tabs={tabsBar}
        dateFilter={
          <DatePeriodFilter
            period={period}
            setPeriod={(val) => {
              setPeriod(val);
              setCurrentPage(1);
            }}
            customFrom={customFrom}
            setCustomFrom={(val) => {
              setCustomFrom(val);
              setCurrentPage(1);
            }}
            customTo={customTo}
            setCustomTo={(val) => {
              setCustomTo(val);
              setCurrentPage(1);
            }}
          />
        }
        headerFilters={secondaryFilters}
        filterFields={
          isLayoutTableTab ? PRINT_LAYOUT_ACTIVE_FILTERS : ORDER_DETAIL_ACTIVE_FILTERS
        }
        filterValues={activeFilterCtrl.filterValues}
        showFilter={activeFilterCtrl.showFilter}
        onToggleFilter={activeFilterCtrl.onToggleFilter}
        onClearAll={() => {
          activeFilterCtrl.onClearAll();
          if (searchQuery) {
            navigate({
              to: "/layouts",
              search: (prev: any) => ({ ...prev, q: undefined }),
              replace: true,
            } as any);
          }
          setUrgencyFilter("ALL");
          setBatchTypeFilter("ALL");
          setPeriod("all");
          setCurrentPage(1);
        }}
        onExport={handleExportExcel}
        actions={
          canEdit ? (
            <Button
              size="sm"
              className="h-8 px-3 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold gap-1.5"
              data-testid="open-new-imposition-btn"
              onClick={() => {
                setModalMode("create");
                setModalLayoutId(null);
                setModalDetails([]);
                setModalOrderCode(null);
                setModalFocusField(null);
                setModalOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Bình File Mới</span>
            </Button>
          ) : undefined
        }
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={setCurrentPage}
        onPageSizeChange={(sz) => {
          setPageSize(sz);
          setCurrentPage(1);
        }}
        isLoading={isLayoutTableTab ? isLayoutsLoading : isDetailsLoading}
      >
        <div
          role="region"
          aria-label="Bảng dữ liệu bình file sản xuất"
          tabIndex={0}
          className="w-full"
        >
          {/* ============================================================== */}
          {/* TAB 1: CHỜ BÌNH (OrderDetails needing imposition / split-run)  */}
          {/* ============================================================== */}
          {activeTab === "cho_binh" && (
            <Table>
              <TableHeader className="apv-table-header">
                <TableRow>
                  <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                  <TableHead
                    className="md:sticky md:left-8 md:z-20 w-[135px] font-semibold text-[11px] uppercase"
                    style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                  >
                    Mã ĐH
                  </TableHead>
                  <TableHead className="w-[165px] font-semibold text-[11px] uppercase">
                    Khách hàng
                  </TableHead>
                  <TableHead className="min-w-[200px] font-semibold text-[11px] uppercase">
                    Sản phẩm & Chất liệu
                  </TableHead>
                  <TableHead className="text-right w-[95px] font-semibold text-[11px] uppercase">
                    SL Cần Bình
                  </TableHead>
                  <TableHead className="w-[175px] font-semibold text-[11px] uppercase">
                    Điền nhanh bài bình
                  </TableHead>
                  <TableHead className="w-[100px] font-semibold text-[11px] uppercase">
                    Hạn giao
                  </TableHead>
                  <TableHead
                    className="md:sticky md:right-0 md:z-20 w-[120px] text-right font-semibold text-[11px] uppercase"
                    style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                  >
                    Thao tác
                  </TableHead>
                </TableRow>
                {detailFilterCtrl.showFilter && (
                  <InlineFilterRow
                    columns={ORDER_DETAIL_FILTER_COLUMNS as any}
                    filterFields={ORDER_DETAIL_ACTIVE_FILTERS}
                    filterValues={detailFilterCtrl.filterValues}
                    onFilterChange={detailFilterCtrl.onFilterChange}
                  />
                )}
              </TableHeader>
              <TableBody>
                {detailItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center text-xs text-slate-500">
                      Không có chi tiết đơn hàng nào đang chờ bình file.
                    </TableCell>
                  </TableRow>
                ) : (
                  detailItems.map((item) => {
                    const idStr = String(item.id);
                    const isSelected = selectedIds.has(idStr);
                    const stickyBg = isSelected ? "#eff6ff" : "#ffffff";
                    const ordCode = resolveOrderCode(item);
                    const ordId = item.orderId || item.order?.id;
                    const mat = extractMaterialSignature(item);
                    const route = extractRoutingSignature(item);
                    const remaining = resolveRemainingPrepressQty(item);
                    const isPartialSplit =
                      Number(item.imposedQty || 0) > 0 && remaining > 0;

                    return (
                      <DataTableRow
                        key={idStr}
                        id={idStr}
                        rowSelection={rowSelection}
                        onClick={() => {
                          if (canEdit) {
                            openImpositionForSingleDetail(item);
                          } else if (ordId) {
                            navigate({
                              to: "/orders/$orderId",
                              params: { orderId: String(ordId) },
                            });
                          }
                        }}
                        className="group"
                        data-testid={`cho-binh-row-${idStr}`}
                      >
                        <DataTableRowCheckbox id={idStr} rowSelection={rowSelection} />
                        <TableCell
                          className="md:sticky md:left-8 md:z-10 max-w-[140px] truncate font-mono text-xs font-bold text-indigo-700"
                          style={{ backgroundColor: stickyBg, isolation: "isolate" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {ordId ? (
                            <EntityLink
                              entityType="order"
                              id={ordId}
                              code={ordCode}
                            />
                          ) : (
                            ordCode || "—"
                          )}
                        </TableCell>

                        <TableCell className="max-w-[170px] whitespace-normal">
                          <div className="text-xs font-semibold text-slate-900 line-clamp-2 leading-snug">
                            {item.order?.customer?.name || item.customerName || "—"}
                          </div>
                        </TableCell>

                        <TableCell className="max-w-[275px] whitespace-normal">
                          <div className="flex items-start gap-2">
                          <ProductionThumbnailCell
                            imageUrl={item.product?.image}
                            layoutUrl={item.printLayout?.fileUrl || item.layoutItems?.[0]?.printLayout?.fileUrl}
                            title={item.productName || item.product?.productName}
                            subtitle={item.productCode || item.product?.productCode}
                            size={36}
                          />
                          <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-slate-900 line-clamp-1">
                            {item.productName || item.product?.productName || "Sản phẩm"}
                          </div>
                          {/* Mã sản phẩm + PO/SO để phân biệt các dòng cùng tên (FB-313HY2) */}
                          {(() => {
                            const code = item.productCode || item.product?.productCode;
                            const po = item.customerPo || item.poReference;
                            const so = item.soReference;
                            if (!code && !po && !so) return null;
                            return (
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[10.5px] text-slate-600" data-testid={`prepress-pool-ids-${item.id}`}>
                                {code && <span className="font-mono font-semibold text-slate-700">{code}</span>}
                                {po && <span>PO: {po}</span>}
                                {so && <span>SO: {so}</span>}
                              </div>
                            );
                          })()}
                          <div className="flex flex-wrap items-center gap-1 mt-0.5 text-[11px] text-slate-500">
                            <Badge
                              variant="outline"
                              className="text-[10px] py-0 h-4 bg-slate-50 border-slate-200 text-slate-700 max-w-[190px] truncate"
                            >
                              {mat.label}
                            </Badge>
                            <span className="truncate max-w-[190px]" title={route.label}>
                              {route.label}
                            </span>
                          </div>
                          </div>
                          </div>
                        </TableCell>

                        <TableCell className="text-right font-mono">
                          <div className="text-xs font-bold text-slate-900">
                            {formatNumber(remaining || item.quantity)}
                          </div>
                          {isPartialSplit && (
                            <div className="text-[10px] font-sans font-semibold text-amber-700">
                              Đã bình {formatNumber(item.imposedQty)} / {formatNumber(item.quantity)}
                            </div>
                          )}
                        </TableCell>

                        <TableCell className="whitespace-normal" onClick={(e) => e.stopPropagation()}>
                          <div className="grid grid-cols-2 gap-1 w-[158px]">
                            {(
                              [
                                { key: "code", label: "+ Mã bài" },
                                { key: "sheetSize", label: "+ Khổ in" },
                                { key: "ups", label: "+ Ups" },
                                { key: "sheets", label: "+ Tờ in" },
                              ] as const
                            ).map((pill) => (
                              <button
                                key={pill.key}
                                type="button"
                                onClick={() => openImpositionForSingleDetail(item, pill.key)}
                                className="px-1.5 py-0.5 rounded border border-dashed border-indigo-200 bg-indigo-50/40 hover:bg-indigo-100/70 hover:border-indigo-400 text-[11px] font-medium text-indigo-700 transition-colors cursor-pointer text-center whitespace-nowrap"
                              >
                                {pill.label}
                              </button>
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="text-xs font-mono text-slate-700">
                            {item.deliveryDeadline || item.order?.deliveryDeadline
                              ? formatDate(item.deliveryDeadline || item.order?.deliveryDeadline)
                              : "—"}
                          </div>
                          <div className="mt-0.5">{renderUrgencyBadge(item)}</div>
                        </TableCell>

                        <TableCell
                          className="md:sticky md:right-0 md:z-10 text-right"
                          style={{ backgroundColor: stickyBg, isolation: "isolate" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            size="sm"
                            variant={isPartialSplit ? "outline" : "default"}
                            onClick={() => openImpositionForSingleDetail(item, "code")}
                            className={cn(
                              "h-7 px-2.5 text-xs font-semibold",
                              isPartialSplit
                                ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                                : "bg-indigo-600 hover:bg-indigo-700 text-white",
                            )}
                            data-testid={`impose-row-btn-${idStr}`}
                          >
                            <Layers className="h-3 w-3 mr-1" />
                            {isPartialSplit ? "Tách thêm bài" : "Bình file"}
                          </Button>
                        </TableCell>
                      </DataTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          {/* ============================================================== */}
          {/* TAB 2 & TAB 5: CHỜ TẠO LỆNH & TẤT CẢ BÀI BÌNH (PrintLayouts)    */}
          {/* ============================================================== */}
          {isLayoutTableTab && (
            <Table>
              <TableHeader className="apv-table-header">
                <TableRow>
                  <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                  <TableHead
                    className="md:sticky md:left-8 md:z-20 w-[180px] font-semibold text-[11px] uppercase"
                    style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                  >
                    Mã Bài Bình & Ảnh
                  </TableHead>
                  <TableHead className="w-[165px] font-semibold text-[11px] uppercase">
                    Đơn Hàng & Khách Hàng
                  </TableHead>
                  <TableHead className="w-[195px] font-semibold text-[11px] uppercase">
                    Sản Phẩm Trong Bài
                  </TableHead>
                  <TableHead className="w-[105px] font-semibold text-[11px] uppercase">
                    Khổ Giấy In
                  </TableHead>
                  <TableHead className="text-center w-[76px] font-semibold text-[11px] uppercase">
                    Số Ups
                  </TableHead>
                  <TableHead className="text-right w-[86px] font-semibold text-[11px] uppercase">
                    SL Tờ In
                  </TableHead>
                  <TableHead className="text-right w-[92px] font-semibold text-[11px] uppercase">
                    Tổng SL SX
                  </TableHead>
                  {activeTab === "tat_ca_bai_binh" && (
                    <TableHead className="w-[96px] font-semibold text-[11px] uppercase">
                      Trạng Thái
                    </TableHead>
                  )}
                  <TableHead
                    className="md:sticky md:right-0 md:z-20 w-[185px] text-right font-semibold text-[11px] uppercase"
                    style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                  >
                    Thao tác
                  </TableHead>
                </TableRow>
                {layoutFilterCtrl.showFilter && (
                  <InlineFilterRow
                    columns={
                      (activeTab === "tat_ca_bai_binh"
                        ? PRINT_LAYOUT_FILTER_COLUMNS
                        : PRINT_LAYOUT_FILTER_COLUMNS.slice(0, -1)) as any
                    }
                    filterFields={PRINT_LAYOUT_ACTIVE_FILTERS}
                    filterValues={layoutFilterCtrl.filterValues}
                    onFilterChange={layoutFilterCtrl.onFilterChange}
                  />
                )}
              </TableHeader>
              <TableBody>
                {layoutItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={activeTab === "tat_ca_bai_binh" ? 10 : 9}
                      className="py-12 text-center text-xs text-slate-500"
                    >
                      {activeTab === "cho_tao_lenh"
                        ? "Không có bài bình nào đang chờ tạo Lệnh sản xuất."
                        : "Chưa có dữ liệu bài bình nào phù hợp bộ lọc."}
                    </TableCell>
                  </TableRow>
                ) : (
                  layoutItems.map((layout) => {
                    const idStr = String(layout.id);
                    const isSelected = selectedIds.has(idStr);
                    const isExpanded = expandedLayoutIds.has(idStr);
                    const stickyBg = isSelected ? "#eff6ff" : "#ffffff";
                    const children: any[] = Array.isArray(layout.layoutItems)
                      ? layout.layoutItems
                      : [];
                    const hasPo = Boolean(layout.productionOrder && !layout.productionOrder.isDeleted);
                    const proofImg = layout.fileUrl ? getFullUrl(layout.fileUrl) : "";
                    const creatorDisplay =
                      layout.creatorName || layout.creator?.fullName || "";

                    return (
                      <React.Fragment key={idStr}>
                        <DataTableRow
                          id={idStr}
                          rowSelection={rowSelection}
                          onClick={() =>
                            navigate({
                              to: "/layouts/$layoutId",
                              params: { layoutId: idStr },
                            })
                          }
                          className="group"
                          data-testid={`layout-row-${idStr}`}
                        >
                          <DataTableRowCheckbox id={idStr} rowSelection={rowSelection} />

                          {/* Mã bài bình + Expand + Proof Thumbnail */}
                          <TableCell
                            className="md:sticky md:left-8 md:z-10 max-w-[180px]"
                            style={{ backgroundColor: stickyBg, isolation: "isolate" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => toggleExpandLayout(idStr)}
                                className="h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500 shrink-0 cursor-pointer"
                                aria-label={isExpanded ? "Thu gọn sản phẩm" : "Mở rộng sản phẩm"}
                                data-testid={`expand-layout-${idStr}`}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5 text-indigo-600" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                )}
                              </button>

                              {proofImg ? (
                                <button
                                  type="button"
                                  onClick={() => setZoomProofUrl(proofImg)}
                                  className="h-8 w-8 rounded border border-slate-200 bg-slate-50 overflow-hidden shrink-0 cursor-pointer hover:ring-2 hover:ring-indigo-400"
                                  title="Xem ảnh sơ đồ bài bình"
                                >
                                  <img
                                    src={proofImg}
                                    alt={layout.layoutCode || "Ảnh bài bình"}
                                    className="h-full w-full object-cover"
                                  />
                                </button>
                              ) : (
                                <div className="h-8 w-8 rounded border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300 shrink-0">
                                  <ImageIcon className="h-3.5 w-3.5" />
                                </div>
                              )}

                              <div className="min-w-0">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate({
                                      to: "/layouts/$layoutId",
                                      params: { layoutId: idStr },
                                    })
                                  }
                                  className="font-mono text-xs font-bold text-indigo-700 hover:underline text-left block truncate max-w-[115px] cursor-pointer"
                                  title={layout.layoutCode || idStr}
                                >
                                  {layout.layoutCode || idStr}
                                </button>
                                <span className="text-[10px] text-slate-400 block truncate max-w-[115px]">
                                  {layout.createdAt ? formatDate(layout.createdAt) : ""}
                                  {creatorDisplay ? ` • ${creatorDisplay}` : ""}
                                </span>
                              </div>
                            </div>
                          </TableCell>

                          {/* Đơn hàng & Khách hàng */}
                          <TableCell
                            className="max-w-[160px] whitespace-normal"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(() => {
                              const firstChildOd = children[0]?.orderDetail;
                              const resolvedOrdId =
                                layout.orderId ||
                                firstChildOd?.orderId ||
                                firstChildOd?.order?.id;
                              const resolvedOrdCode =
                                layout.orderCode || resolveOrderCode(firstChildOd);
                              const resolvedCustomer =
                                layout.customerName ||
                                firstChildOd?.order?.customer?.name ||
                                "—";
                              return (
                                <>
                                  <div className="font-mono text-xs font-semibold text-indigo-700 truncate">
                                    {resolvedOrdId && resolvedOrdCode ? (
                                      <EntityLink
                                        entityType="order"
                                        id={resolvedOrdId}
                                        code={resolvedOrdCode}
                                      />
                                    ) : (
                                      resolvedOrdCode || "—"
                                    )}
                                  </div>
                                  <div className="text-xs font-medium text-slate-800 line-clamp-1 mt-0.5">
                                    {resolvedCustomer}
                                  </div>
                                </>
                              );
                            })()}
                          </TableCell>

                          {/* Sản phẩm & Phân loại */}
                          <TableCell className="max-w-[190px] whitespace-normal">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {layout.batchType === "GANG_RUN" ||
                              layout.isGangRun ||
                              children.length > 1 ? (
                                <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-[10px] font-bold py-0">
                                  Ghép {layout.itemCount || children.length} SP
                                </Badge>
                              ) : layout.batchType === "SPLIT_RUN" || layout.isSplitRun ? (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold py-0">
                                  Tách bài
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] py-0 text-slate-600">
                                  Bài đơn
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs font-medium text-slate-800 line-clamp-2 mt-0.5 leading-snug">
                              {children
                                .map(
                                  (li: any) =>
                                    li.orderDetail?.productName ||
                                    li.orderDetail?.product?.productName,
                                )
                                .filter(Boolean)
                                .join(" + ") ||
                                layout.name ||
                                "—"}
                            </div>
                            {/* Mã sản phẩm để phân biệt sản phẩm cùng tên (FB-1GS4DJ) */}
                            {(() => {
                              const codes = children
                                .map((li: any) => li.orderDetail?.productCode || li.orderDetail?.product?.productCode)
                                .filter(Boolean);
                              if (!codes.length) return null;
                              const text = codes.join(" · ");
                              return (
                                <div className="font-mono text-[10.5px] font-semibold text-slate-600 line-clamp-1 mt-0.5" title={text} data-testid={`layout-product-codes-${layout.id}`}>
                                  {text}
                                </div>
                              );
                            })()}
                          </TableCell>

                          {/* Khổ giấy in */}
                          <TableCell className="font-mono text-xs font-semibold text-slate-800">
                            {layout.sheetSize || "—"}
                          </TableCell>

                          {/* Tổng Ups */}
                          <TableCell className="text-center">
                            <Badge
                              variant="outline"
                              className="font-mono font-bold text-[11px] bg-indigo-50/60 text-indigo-700 border-indigo-200 px-1.5"
                            >
                              {formatNumber(layout.totalUps || layout.ups || 1)} ups
                            </Badge>
                          </TableCell>

                          {/* SL tờ in / Ups */}
                          <TableCell className="text-right font-mono text-xs font-bold text-slate-900">
                            {formatNumber(layout.sheetsPerUp || layout.totalPrintSheets || 0)}
                          </TableCell>

                          {/* Tổng SL SX */}
                          <TableCell className="text-right font-mono text-xs font-bold text-emerald-700">
                            {formatNumber(layout.totalPlannedQty || 0)}
                          </TableCell>

                          {/* Trạng thái / LSX (chỉ hiển thị trên Tất cả bài bình) */}
                          {activeTab === "tat_ca_bai_binh" && (
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {hasPo ? (
                                <div className="space-y-0.5">
                                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] font-bold">
                                    Đã tạo lệnh
                                  </Badge>
                                  <div className="font-mono text-[11px] font-bold text-indigo-700">
                                    <EntityLink
                                      entityType="productionOrder"
                                      id={layout.productionOrder.id}
                                      code={layout.productionOrder.orderId}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <Badge className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] font-semibold">
                                  Chờ tạo lệnh
                                </Badge>
                              )}
                            </TableCell>
                          )}

                          {/* Thao tác */}
                          <TableCell
                            className="md:sticky md:right-0 md:z-10 text-right"
                            style={{ backgroundColor: stickyBg, isolation: "isolate" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="inline-flex items-center justify-end gap-1">
                              {!hasPo && canEdit && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditLayoutModal(layout)}
                                    className="h-7 px-2 text-xs"
                                    title="Sửa bài bình"
                                    data-testid={`edit-layout-btn-${idStr}`}
                                  >
                                    <Pencil className="h-3 w-3 mr-1" /> Sửa
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      navigate({
                                        to: "/production-orders/create",
                                        search: { layoutId: idStr } as any,
                                      })
                                    }
                                    className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                                    data-testid={`create-po-from-layout-btn-${idStr}`}
                                  >
                                    Tạo Lệnh SX <ArrowRight className="h-3 w-3 ml-0.5" />
                                  </Button>
                                  {canDelete && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        setLayoutToDelete({
                                          id: idStr,
                                          code: layout.layoutCode || idStr,
                                        })
                                      }
                                      className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                                      title="Xóa bài bình"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </>
                              )}
                              {hasPo && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    navigate({
                                      to: "/layouts/$layoutId",
                                      params: { layoutId: idStr },
                                    })
                                  }
                                  className="h-7 px-2.5 text-xs"
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" /> Chi tiết
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </DataTableRow>

                        {/* Expanded Child Rows (`PrintLayoutItem` breakdown) */}
                        {isExpanded && children.length > 0 && (
                          <TableRow className="bg-slate-50/90 hover:bg-slate-50/90">
                            <TableCell
                              colSpan={activeTab === "tat_ca_bai_binh" ? 10 : 9}
                              className="p-0 whitespace-normal"
                            >
                              <div className="px-6 py-2.5 border-y border-indigo-100 bg-indigo-50/30">
                                <table className="w-full max-w-[1090px] text-xs">
                                  <thead>
                                    <tr className="text-[10px] font-bold uppercase text-slate-500 border-b border-indigo-100">
                                      <th className="text-left py-1.5 pr-2 w-[130px]">Mã ĐH</th>
                                      <th className="text-left py-1.5 px-2 w-[210px]">Sản phẩm</th>
                                      <th className="text-left py-1.5 px-2">Chất liệu & Quy trình</th>
                                      <th className="text-right py-1.5 px-2 w-[90px]">SL Đặt</th>
                                      <th className="text-center py-1.5 px-2 w-[78px]">Số Ups</th>
                                      <th className="text-right py-1.5 px-2 w-[85px]">SL Tờ in</th>
                                      <th className="text-right py-1.5 pl-2 w-[110px]">SL SX trong bài</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-indigo-100/60">
                                    {children.map((li: any) => {
                                      const od = li.orderDetail;
                                      const mat = extractMaterialSignature(od);
                                      const route = extractRoutingSignature(od);
                                      return (
                                        <tr key={li.id}>
                                          <td className="py-1.5 pr-2 font-mono font-semibold text-indigo-700 truncate">
                                            {resolveOrderCode(od) || "—"}
                                          </td>
                                          <td className="py-1.5 px-2 font-semibold text-slate-900 whitespace-normal">
                                            {od?.productName || od?.product?.productName || "—"}
                                            {(od?.productCode || od?.product?.productCode) && (
                                              <div className="font-mono text-[10.5px] font-semibold text-slate-600">
                                                {od?.productCode || od?.product?.productCode}
                                              </div>
                                            )}
                                          </td>
                                          <td className="py-1.5 px-2 text-slate-600 whitespace-normal">
                                            {mat.label} • {route.label}
                                          </td>
                                          <td className="py-1.5 px-2 text-right font-mono text-slate-700">
                                            {formatNumber(od?.quantity || 0)}
                                          </td>
                                          <td className="py-1.5 px-2 text-center font-mono font-bold text-indigo-700">
                                            {formatNumber(li.ups || 1)} ups
                                          </td>
                                          <td className="py-1.5 px-2 text-right font-mono text-slate-800">
                                            {formatNumber(
                                              li.sheetsPerUp || layout.sheetsPerUp || 0,
                                            )}
                                          </td>
                                          <td className="py-1.5 pl-2 text-right font-mono font-bold text-emerald-700">
                                            {formatNumber(li.plannedQty || 0)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          {/* ============================================================== */}
          {/* TAB 3: ĐÃ TẠO LỆNH (Dispatched OrderDetails with Split/Gang)   */}
          {/* ============================================================== */}
          {activeTab === "da_tao_lenh" && (
            <Table>
              <TableHeader className="apv-table-header">
                <TableRow>
                  <TableHead
                    className="md:sticky md:left-0 md:z-20 w-[130px] font-semibold text-[11px] uppercase"
                    style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                  >
                    Mã ĐH
                  </TableHead>
                  <TableHead className="min-w-[165px] font-semibold text-[11px] uppercase">
                    Khách hàng
                  </TableHead>
                  <TableHead className="min-w-[180px] font-semibold text-[11px] uppercase">
                    Sản phẩm
                  </TableHead>
                  <TableHead className="min-w-[165px] font-semibold text-[11px] uppercase">
                    Mã Bài Bình
                  </TableHead>
                  <TableHead className="w-[115px] font-semibold text-[11px] uppercase">
                    Khổ Giấy In
                  </TableHead>
                  <TableHead className="text-center w-[75px] font-semibold text-[11px] uppercase">
                    Số Ups
                  </TableHead>
                  <TableHead className="text-right w-[115px] font-semibold text-[11px] uppercase">
                    SL Đã Bình
                  </TableHead>
                  <TableHead className="text-right w-[95px] font-semibold text-[11px] uppercase">
                    SL Tờ In
                  </TableHead>
                  <TableHead className="w-[115px] font-semibold text-[11px] uppercase">
                    Người Bình File
                  </TableHead>
                  <TableHead className="w-[130px] font-semibold text-[11px] uppercase">
                    Lệnh Sản Xuất
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-12 text-center text-xs text-slate-500">
                      Chưa có chi tiết đơn hàng nào đã tạo Lệnh sản xuất trong kỳ này.
                    </TableCell>
                  </TableRow>
                ) : (
                  detailItems.map((item) => {
                    const ordCode = resolveOrderCode(item);
                    const ordId = item.orderId || item.order?.id;
                    const itemLayouts: any[] = Array.isArray(item.layoutItems)
                      ? item.layoutItems.filter((li: any) => !li.isDeleted && li.printLayout)
                      : [];
                    const creatorName =
                      itemLayouts[0]?.printLayout?.creator?.fullName ||
                      item.printLayout?.creator?.fullName ||
                      "—";
                    const prodOrders: any[] = Array.isArray(item.productionOrderSources)
                      ? item.productionOrderSources
                          .map((s: any) => s.productionOrder)
                          .filter(Boolean)
                      : [];

                    return (
                      <TableRow key={item.id} className="hover:bg-slate-50/70">
                        <TableCell
                          className="md:sticky md:left-0 md:z-10 font-mono text-xs font-bold text-indigo-700"
                          style={{ backgroundColor: "#ffffff", isolation: "isolate" }}
                        >
                          {ordId ? (
                            <EntityLink entityType="order" id={ordId} code={ordCode} />
                          ) : (
                            ordCode || "—"
                          )}
                        </TableCell>

                        <TableCell className="text-xs font-medium text-slate-900 whitespace-normal">
                          {item.order?.customer?.name || "—"}
                        </TableCell>

                        <TableCell className="text-xs font-semibold text-slate-900 whitespace-normal">
                          <div className="flex items-center gap-2">
                            <ProductionThumbnailCell
                              imageUrl={item.product?.image}
                              layoutUrl={item.printLayout?.fileUrl || item.layoutItems?.[0]?.printLayout?.fileUrl}
                              title={item.productName || item.product?.productName}
                              subtitle={item.productCode || item.product?.productCode}
                              size={32}
                            />
                            <span>{item.productName || item.product?.productName || "—"}</span>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            {itemLayouts.length > 0 ? (
                              itemLayouts.map((li: any) => (
                                <button
                                  key={li.id}
                                  type="button"
                                  onClick={() =>
                                    navigate({
                                      to: "/layouts/$layoutId",
                                      params: { layoutId: li.printLayout.id },
                                    })
                                  }
                                  className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-mono text-[11px] font-bold hover:bg-indigo-100 cursor-pointer"
                                >
                                  {li.printLayout.layoutCode}
                                </button>
                              ))
                            ) : (
                              <span className="font-mono text-xs font-bold text-indigo-700">
                                {item.prepressCode || item.printLayout?.layoutCode || "—"}
                              </span>
                            )}
                            {itemLayouts.length > 1 && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0">
                                Tách {itemLayouts.length} bài
                              </Badge>
                            )}
                          </div>
                        </TableCell>

                        <TableCell className="font-mono text-xs text-slate-700">
                          {item.prepressPaperSize || item.printLayout?.sheetSize || "—"}
                        </TableCell>

                        <TableCell className="text-center font-mono text-xs font-bold text-indigo-700">
                          {item.prepressUps || item.printLayout?.ups || "—"}
                        </TableCell>

                        <TableCell className="text-right font-mono text-xs font-bold text-emerald-700">
                          {formatNumber(
                            Math.max(Number(item.imposedQty || 0), Number(item.plannedQty || 0)) ||
                              item.quantity,
                          )}
                          <span className="text-[10px] text-slate-400 font-normal">
                            {" "}
                            / {formatNumber(item.quantity)}
                          </span>
                        </TableCell>

                        <TableCell className="text-right font-mono text-xs text-slate-800">
                          {formatNumber(item.prepressPrintSheets || 0)}
                        </TableCell>

                        <TableCell className="text-xs text-slate-700">{creatorName}</TableCell>

                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {prodOrders.length > 0 ? (
                              prodOrders.map((po: any) => (
                                <EntityLink
                                  key={po.id}
                                  entityType="productionOrder"
                                  id={po.id}
                                  code={po.orderId}
                                />
                              ))
                            ) : item.productionOrder ? (
                              <EntityLink
                                entityType="productionOrder"
                                id={item.productionOrder.id}
                                code={item.productionOrder.orderId}
                              />
                            ) : (
                              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                                Đã tạo lệnh
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          {/* ============================================================== */}
          {/* TAB 4: YÊU CẦU BÌNH LẠI (Re-prepress queue)                    */}
          {/* ============================================================== */}
          {activeTab === "yeu_cau_binh_lai" && (
            <Table>
              <TableHeader className="apv-table-header">
                <TableRow>
                  <TableHead className="w-[125px] font-semibold text-[11px] uppercase">
                    Mã ĐH
                  </TableHead>
                  <TableHead className="min-w-[180px] font-semibold text-[11px] uppercase">
                    Khách hàng
                  </TableHead>
                  <TableHead className="min-w-[200px] font-semibold text-[11px] uppercase">
                    Sản phẩm
                  </TableHead>
                  <TableHead className="min-w-[220px] font-semibold text-[11px] uppercase">
                    Lý do yêu cầu bình lại
                  </TableHead>
                  <TableHead className="w-[150px] font-semibold text-[11px] uppercase">
                    Bài bình hiện tại
                  </TableHead>
                  <TableHead className="text-right w-[220px] font-semibold text-[11px] uppercase">
                    Thao tác
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-xs text-slate-500">
                      Không có yêu cầu bình lại nào đang chờ xử lý.
                    </TableCell>
                  </TableRow>
                ) : (
                  detailItems.map((item) => {
                    const ordCode = resolveOrderCode(item);
                    const ordId = item.orderId || item.order?.id;
                    // Bài bình đã lên Lệnh SX thì form bình không lưu được (máy chủ chặn sửa bài đã lên lệnh);
                    // dòng đó chỉ cập nhật thông số bình lại bằng Xử lý nhanh (FB-ROS5VX)
                    const lineLayouts = new Map<string, any>();
                    for (const l of [item.printLayout, ...(item.layoutItems || []).map((li: any) => li.printLayout)]) {
                      if (l?.id && (!lineLayouts.has(l.id) || l.productionOrder)) lineLayouts.set(l.id, l);
                    }
                    const lockingLayout = Array.from(lineLayouts.values()).find((l: any) =>
                      l.productionOrder
                        ? !l.productionOrder.isDeleted && l.productionOrder.status !== "CANCELLED"
                        : Boolean(l.productionOrderId),
                    );
                    const lockingLsxCode =
                      lockingLayout?.productionOrder?.orderId ||
                      item.productionOrderSources?.[0]?.productionOrder?.orderId ||
                      "";
                    return (
                      <TableRow key={item.id} className="hover:bg-rose-50/20">
                        <TableCell className="font-mono text-xs font-bold text-indigo-700">
                          {ordId ? (
                            <EntityLink entityType="order" id={ordId} code={ordCode} />
                          ) : (
                            ordCode || "—"
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-medium text-slate-900">
                          {item.order?.customer?.name || "—"}
                        </TableCell>
                        <TableCell className="text-xs font-semibold text-slate-900">
                          <div className="flex items-center gap-2">
                            <ProductionThumbnailCell
                              imageUrl={item.product?.image}
                              layoutUrl={item.printLayout?.fileUrl || item.layoutItems?.[0]?.printLayout?.fileUrl}
                              title={item.productName || item.product?.productName}
                              subtitle={item.productCode || item.product?.productCode}
                              size={32}
                            />
                            <span>{item.productName || item.product?.productName || "—"}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs font-medium text-rose-700">
                            {item.rePrepressReason || "Yêu cầu kiểm tra / bình lại"}
                          </div>
                          {item.rePrepressRequestedAt && (
                            <div className="text-[10px] text-slate-400">
                              <Clock className="h-2.5 w-2.5 inline mr-0.5" />
                              {formatDate(item.rePrepressRequestedAt)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-700">
                          {item.prepressCode || item.printLayout?.layoutCode || "—"}
                          {lockingLayout && (
                            <div className="text-[10px] font-sans text-amber-700" data-testid="reprepress-locked-lsx">
                              Đã lên {lockingLsxCode || "lệnh SX"}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <span
                              title={
                                lockingLayout
                                  ? `Bài bình đã lên ${lockingLsxCode || "lệnh SX"} nên không sửa bằng form bình. Bấm Xử lý nhanh để cập nhật thông số bình lại.`
                                  : undefined
                              }
                            >
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={Boolean(lockingLayout)}
                                onClick={() => openImpositionForSingleDetail(item, "code")}
                                className="h-7 px-2.5 text-xs"
                                data-testid="reprepress-open-form"
                              >
                                <Layers className="h-3 w-3 mr-1" /> Mở Form Bình
                              </Button>
                            </span>
                            <Button
                              size="sm"
                              onClick={() => {
                                setRePrepressTarget(item);
                                setReForm({
                                  prepressCode:
                                    item.prepressCode || item.printLayout?.layoutCode || "",
                                  sheetSize:
                                    item.prepressPaperSize || item.printLayout?.sheetSize || "",
                                  ups: Number(item.prepressUps || item.printLayout?.ups || 1),
                                  printSheets: Number(item.prepressPrintSheets || 1),
                                  notes: "",
                                });
                              }}
                              className="h-7 px-2.5 text-xs bg-rose-600 hover:bg-rose-700 text-white font-semibold"
                            >
                              <Repeat className="h-3 w-3 mr-1" /> Xử lý nhanh
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </ListPageShell>

      {/* Universal Floating Bulk Action Bar (Rule 9) */}
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />

      {/* Canonical PrintLayout Modal Container (Rule 2) */}
      <PrintLayoutModalContainer
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) {
            setModalLayoutId(null);
            setModalDetails([]);
            setModalFocusField(null);
          }
        }}
        mode={modalMode}
        layoutId={modalLayoutId}
        initialDetails={modalDetails}
        orderCode={modalOrderCode}
        focusField={modalFocusField}
        onSuccess={async () => {
          clearSelection();
          await refreshAllPrepressQueries();
        }}
      />

      {/* Quick Re-Prepress Confirm Dialog */}
      <Dialog
        open={Boolean(rePrepressTarget)}
        onOpenChange={(o) => !o && setRePrepressTarget(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Xử lý yêu cầu bình lại</DialogTitle>
            <DialogDescription>
              Cập nhật thông số bài bình cho sản phẩm{" "}
              <strong>{rePrepressTarget?.productName}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2.5">
              <div className="space-y-1">
                <Label className="text-xs">Mã bài bình mới</Label>
                <Input
                  value={reForm.prepressCode}
                  onChange={(e) => setReForm((p) => ({ ...p, prepressCode: e.target.value }))}
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  className="h-8 text-xs font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Khổ giấy in</Label>
                <Input
                  value={reForm.sheetSize}
                  onChange={(e) => setReForm((p) => ({ ...p, sheetSize: e.target.value }))}
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div className="space-y-1">
                <Label className="text-xs">Số Ups</Label>
                <NumericInput
                  value={reForm.ups > 0 ? reForm.ups : ""}
                  allowNegative={false}
                  decimalScale={0}
                  onValueChange={(values, sourceInfo) => {
                    if (sourceInfo.source !== "event") return;
                    setReForm((p) => ({
                      ...p,
                      ups: values.floatValue ? Math.max(1, Math.round(values.floatValue)) : 0,
                    }));
                  }}
                  onBlur={() =>
                    setReForm((p) => ({ ...p, ups: p.ups >= 1 ? p.ups : 1 }))
                  }
                  className="h-8 text-xs font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">SL tờ in mỗi Ups</Label>
                <NumericInput
                  value={reForm.printSheets > 0 ? reForm.printSheets : ""}
                  allowNegative={false}
                  decimalScale={0}
                  onValueChange={(values, sourceInfo) => {
                    if (sourceInfo.source !== "event") return;
                    setReForm((p) => ({
                      ...p,
                      printSheets: values.floatValue
                        ? Math.max(1, Math.round(values.floatValue))
                        : 0,
                    }));
                  }}
                  onBlur={() =>
                    setReForm((p) => ({
                      ...p,
                      printSheets: p.printSheets >= 1 ? p.printSheets : 1,
                    }))
                  }
                  className="h-8 text-xs font-mono"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ghi chú xử lý</Label>
              <Textarea
                rows={2}
                value={reForm.notes}
                onChange={(e) => setReForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Ghi chú nội dung đã điều chỉnh..."
                className="text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRePrepressTarget(null)}>
              Hủy
            </Button>
            <Button
              size="sm"
              disabled={resolveRePrepressMutation.isPending}
              onClick={() => {
                if (!rePrepressTarget) return;
                resolveRePrepressMutation.mutate({
                  detailIds: [String(rePrepressTarget.id)],
                  prepressCode: reForm.prepressCode.trim() || undefined,
                  sheetSize: reForm.sheetSize.trim() || undefined,
                  ups: reForm.ups,
                  printSheets: reForm.printSheets,
                  notes: reForm.notes.trim() || undefined,
                });
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {resolveRePrepressMutation.isPending && (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              )}
              Xác Nhận Hoàn Tất
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation */}
      <AlertDialog
        open={Boolean(layoutToDelete)}
        onOpenChange={(o) => !o && setLayoutToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xóa bài bình {layoutToDelete?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              Các chi tiết đơn hàng thuộc bài bình này sẽ được hoàn lại số lượng về danh sách{" "}
              <strong>Chờ bình</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteLayoutMutation.isPending}
              onClick={() => layoutToDelete && deleteLayoutMutation.mutate(layoutToDelete.id)}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Xóa bài bình
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xóa {selectedCount} bài bình đã chọn?</AlertDialogTitle>
            <AlertDialogDescription>
              Hệ thống sẽ xóa các bài bình chưa tạo Lệnh sản xuất và hoàn lại số lượng chờ bình cho đơn hàng. Các bài đã có Lệnh sản xuất sẽ tự động được bảo vệ.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkDeleteLayoutsMutation.isPending}
              onClick={() => bulkDeleteLayoutsMutation.mutate(Array.from(selectedIds))}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Xác nhận xóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Proof Image Lightbox */}
      <Dialog open={Boolean(zoomProofUrl)} onOpenChange={(o) => !o && setZoomProofUrl(null)}>
        <DialogContent className="max-w-4xl p-3">
          <DialogHeader>
            <DialogTitle className="text-sm">Ảnh sơ đồ bài bình</DialogTitle>
          </DialogHeader>
          {zoomProofUrl && (
            <div className="flex items-center justify-center bg-slate-50 rounded-lg p-2 max-h-[80vh] overflow-auto">
              <img
                src={zoomProofUrl}
                alt="Ảnh sơ đồ bài bình"
                className="max-h-[75vh] max-w-full object-contain rounded"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
